import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useRef, useState, useEffect } from 'react';
import homeFill from '@iconify/icons-eva/home-fill';
import personFill from '@iconify/icons-eva/person-fill';
import searchFill from '@iconify/icons-eva/search-fill';
import logOutFill from '@iconify/icons-eva/log-out-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Divider, InputAdornment, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { MIconButton } from '../../components/@material-extend';
import MyAvatar from '../../components/MyAvatar';
import MenuPopover from '../../components/MenuPopover';
import Scrollbar from '../../components/Scrollbar';
import { getSimpleCompagnies, getApp, getLotGagnants, getAppJournalier } from '../../redux/slices/user';
import { useDispatch, useSelector } from '../../redux/store';
import Request from '../../utils/Request';
import { useTranslation } from 'react-i18next';

const AccountPopover = () => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const { user, logout, initialize } = useAuth();
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState(false);
  const [search, setSearch] = useState('');
  const { compagnies } = useSelector((state) => state.user);

  const MENU_OPTIONS = [
    {
      label: t('account_popover.menu_options.home'),
      icon: homeFill,
      linkTo: '/'
    },
    {
      label: t('account_popover.menu_options.profile'),
      icon: personFill,
      linkTo: PATH_DASHBOARD.user?.account
    }
  ];

  const handleOpen = () => {
    setOpen(true);
    if (user?.role === 'superuser' && (!compagnies || compagnies.length === 0)) {
      dispatch(getSimpleCompagnies());
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar(t('account_popover.logout_error'), { variant: 'error' });
    }
  };

  const handleChoose = async (refcode) => {
    setLoad(true);
    const values = {
      ref_code: refcode
    };
    await Request.handleChooseCompany(values);
    await initialize();
    dispatch(getApp());
    dispatch(getAppJournalier());
    dispatch(getLotGagnants());
    handleClose();
  };

  const displayName = user?.role === 'company' ? user?.companyname : `${user?.first_name} ${user?.last_name}`;
  
  let roleName = t('account_popover.roles.unknown');
  if (user?.role === 'company') {
    roleName = t('account_popover.roles.admin');
  } else if (user?.role === 'supervisor') {
    roleName = t('account_popover.roles.supervisor');
  } else if (user?.role === 'superuser') {
    roleName = t('account_popover.roles.super_admin');
  }

  // insensible a la casse et aux accents
  const normalize = (value) =>
    `${value ?? ''}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const companyOptions = [
    {
      ref_code: 'all',
      user_photo: '/static/icons/navbar/ic_surcussale.svg',
      companyname: t('account_popover.all_companies')
    },
    ...compagnies
  ];

  const filteredCompagnies = companyOptions.filter((comp) => normalize(comp.companyname).includes(normalize(search)));

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar compagnies={compagnies} />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: user?.role === 'superuser' ? 280 : 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {roleName}
            </Typography>
          </Box>

          <Tooltip title={t('account_popover.logout_button')}>
            <MIconButton color="inherit" onClick={handleLogout} sx={{ ml: 1, flexShrink: 0 }}>
              <Box component={Icon} icon={logOutFill} sx={{ width: 22, height: 22 }} />
            </MIconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ my: 1 }} />
        {user?.role === 'superuser' && (
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              autoComplete="off"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('account_popover.search_placeholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component={Icon} icon={searchFill} sx={{ width: 20, height: 20, color: 'text.disabled' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>
        )}
        {user?.role !== 'superuser' ? (
          MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={{ typography: 'body2', py: 1, px: 2.5 }}
            >
              <Box
                component={Icon}
                icon={option.icon}
                sx={{
                  mr: 2,
                  width: 24,
                  height: 24
                }}
              />

              {option.label}
            </MenuItem>
          ))
        ) : (
          <Scrollbar sx={{ height: 450 }}>
            {filteredCompagnies.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', px: 2.5, py: 2 }} noWrap>
                {t('account_popover.no_result')}
              </Typography>
            )}
            {filteredCompagnies.map((comp) => (
              <MenuItem
                key={comp.ref_code}
                onClick={() => handleChoose(comp.ref_code)}
                sx={{
                  typography: 'body2',
                  py: 1,
                  px: 2.5,
                  ...(comp.ref_code === user.select_company && {
                    bgcolor: theme.palette.primary.main
                  }),
                  '&:hover': {
                    bgcolor: theme.palette.secondary.main,
                    color: 'common.black'
                  }
                }}
              >
                <img
                  src={comp.user_photo}
                  alt="logo"
                  loading="lazy"
                  style={{ marginRight: 8, width: 30, height: 30, borderRadius: 4, objectFit: 'cover' }}
                />
                <Typography
                  sx={{
                    ...(comp.ref_code === user.select_company && {
                      color: 'common.white'
                    })
                  }}
                >
                  {comp.companyname}
                </Typography>
              </MenuItem>
            ))}
          </Scrollbar>
        )}
      </MenuPopover>
    </>
  );
};

export default AccountPopover;